import { PurchaseStatementDetails, purchaseStatementsService } from '@store/invoices/purchaseStatements';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import ModalComponent from '@components/modal/Modal.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { ExecutionLine } from '@store/invoices/executionLines';
import { GridRowParams, GridValueFormatterParams, GridValueGetterParams } from '@mui/x-data-grid-pro';
import { NumberExt } from '@utils/extensions/number.extension';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CheckCircleGreen, CheckCircleGrey } from '@icons/index';
import NewTableComponent from '@components/table/NewTable.component';
import ConfirmModal from '@components/modal/Confirm.modal';
import { Colors } from '@constants/colors.constants';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';

interface CancelPurchaseStatementModalProps {
  purchaseStatementDetails?: Partial<PurchaseStatementDetails>;
  handleClose: () => void;
}

const CancelPurchaseStatementModal = (props: CancelPurchaseStatementModalProps) => {
  const { handleClose, purchaseStatementDetails } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [reason, setReason] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState<number[]>([]);

  const searchedExecutionLines = useMemo(() => purchaseStatementDetails?.supplierInvoiceLines ?? [], [purchaseStatementDetails?.supplierInvoiceLines]);

  const selectedLines = searchedExecutionLines.filter(line => selectedInvoiceLines.includes(line.id));

  const handleCancelPurchaseStatement = () => {
    if (purchaseStatementDetails?.id) {
      purchaseStatementsService.cancelPurchaseStatement(purchaseStatementDetails.id, selectedInvoiceLines, reason, invoiceNumber).subscribe({
        next: (value) => {
          handleClose();
          navigate(`/invoices/purchase-statement/${value.id}`);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  useEffect(() => {
    if (purchaseStatementDetails?.id) {
      setInvoiceNumber(purchaseStatementDetails.invoiceNumber as string);
    }
  }
    , [purchaseStatementDetails]);
  return (
    <>
      <ModalComponent
        handleClose={handleClose}
        maxWidth={false}
        title={t('invoices.purchaseStatement.cancelModal.title')}
        content={(
          <Stack pt="30px" pb="25px" height="85vh" width="100%" spacing={2}>
            <NewTableComponent
              columns={[
                COLUMN_TYPES.DATE({
                  field: 'execution.executedAt',
                  cellClassName: () => 'secondary',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.executedAt'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.executedAt,
                  valueFormatter: (params: GridValueFormatterParams) =>
                    params.value ? t('global.date', { date: new Date(params.value) }) : '',
                  width: 150,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'name',
                  color: 'secondary',
                  label: t('invoices.executions.table.name'),
                  fullWidth: true,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'serviceKind.kind',
                  cellClassName: () => 'bold',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.type'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'additionalInformation',
                  color: 'secondary',
                  label: t('invoices.executions.table.additionalInformation'),
                  minWidth: 100,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'waste',
                  color: 'secondary',
                  label: t('invoices.executions.table.waste'),
                  // valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'container',
                  color: 'secondary',
                  label: t('invoices.executions.table.container'),
                  // valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.container,
                  width: 200,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'executionLine.quantity',
                  label: t('invoices.executions.table.quantity'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                  width: 100,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'serviceKind.execUnitName1',
                  color: 'secondary',
                  label: t('invoices.executions.table.unit'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.unit,
                  width: 100,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'executionLine.puBuy',
                  label: t('invoices.executions.table.puBuy'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                    params.row.puBuy ? NumberExt.round(params.row.puBuy / 100, 2) : undefined,
                  width: 100
                }),
                COLUMN_TYPES.TEXT({
                  field: 'totalBuy',
                  label: t('invoices.executions.table.totalBuy'),
                  valueFormatter: (params: GridValueFormatterParams) =>
                    params.value ? NumberExt.round(params.value / 100, 2) : '',
                  width: 100,
                }),
                COLUMN_TYPES.ICON({
                  field: 'clientInvoiceLineId',
                  label: t('invoices.executions.table.billsShort'),
                  renderIcon: (executionLine: ExecutionLine) => {
                    if (!executionLine.supplierInvoicingRequired) return <Box>-</Box>;
                    return executionLine.supplierInvoiceLineId ? <CheckCircleGreen /> : <CheckCircleGrey />;
                  },
                  width: 40,
                }),
              ]}
              paginationMode="client"
              disablePagination
              sortingMode="client"
              autoHeight={false}
              sortModel={[
                { field: 'execution.executedAt', sort: 'asc' },
                { field: 'waste', sort: 'asc' },
                {
                  field: 'container',
                  sort: 'asc',
                },
              ]}
              isRowSelectable={(params: GridRowParams<ExecutionLine>) => params.row.cancelledByLineId === null}
              handleRowSelected={setSelectedInvoiceLines}
              rowsSelected={selectedInvoiceLines}
              data={searchedExecutionLines} />
            <Stack spacing={2} direction="column" bgcolor={Colors.white} p={2}>
              <Stack direction="row" spacing={2}>
                <Stack spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.bills.cancelModal.originRef')}
                  </Typography>
                  <TextFieldComponent
                    inputWidth={250}
                    readOnly
                    value={purchaseStatementDetails?.invoiceRef} />
                </Stack>
                <Stack flex={0.5} spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.purchaseStatement.details.billNumber')}*
                  </Typography>
                  <TextFieldComponent
                    handleChange={(value) => setInvoiceNumber(value)}
                    value={invoiceNumber}
                  />
                </Stack>
                <Stack spacing={0.5} flex={1}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.bills.cancelModal.reason')}
                  </Typography>
                  <TextFieldComponent
                    handleChange={(value) => setReason(value)}
                    value={reason} />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Stack spacing={0.5}>
                    <Typography fontSize="16px" fontWeight="400">
                      {t('invoices.bills.details.totalAmountNoVatInCents')}
                    </Typography>
                    <NumberTextFieldComponent
                      readOnly
                      inputWidth={250}
                      endAdornment={
                        <Typography fontSize={14} fontWeight="400">
                          {purchaseStatementDetails?.currency}
                        </Typography>
                      }
                      value={NumberExt.round((purchaseStatementDetails?.totalAmountNoVatInCents ?? 0) / 100, 2)}
                    />
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography fontSize="16px" fontWeight="400">
                      {t('invoices.bills.cancelModal.cancelAmountVatIncluded')}
                    </Typography>
                    <NumberTextFieldComponent
                      readOnly
                      inputWidth={250}
                      endAdornment={
                        <Typography fontSize={14} fontWeight="400">
                          {purchaseStatementDetails?.currency}
                        </Typography>
                      }
                      value={NumberExt.round((selectedLines.reduce((acc, val) => acc + val.totalBuy, 0) ?? 0) / 100, 2)}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )}
        actions={(
          <>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              {t('global.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedInvoiceLines?.length}
              onClick={() => setShowConfirmModal(true)}
            >
              {t('global.validate')}
            </Button>
          </>
        )}
      />
      {showConfirmModal && (<ConfirmModal title={t('invoices.bills.cancelModal.confirmModal.title')} description={t('invoices.bills.cancelModal.confirmModal.body')} handleClose={() => setShowConfirmModal(false)} handleValidate={() => handleCancelPurchaseStatement()} validateButton={t('invoices.bills.cancelModal.confirmModal.button')} />)}
    </>
  );
};

export default CancelPurchaseStatementModal;
